import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
@Component({
  selector: 'app-audio-video-setup',
  templateUrl: './audio-video-setup.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioVideoSetupComponent implements AfterViewInit, OnDestroy {
  // ===================== Inputs / Outputs =====================

  @Input() recordSeconds = 30;
  @Input() minRecordSeconds = 10;
  @Input() requireConsent = false;
  @Input() voiceThreshold = 12;
  @Input() showContinueButton = false;
  @Input() continueButtonDisabled = true;
  @Output() testPassed = new EventEmitter<boolean>();
  @Output() recordingCompleted = new EventEmitter<boolean>();
  @Output() continueClicked = new EventEmitter<void>();

  @ViewChild('previewVideo') previewVideo?: ElementRef<HTMLVideoElement>;

  // ===================== Tuning constants =====================

  // -- Level meter --
  private readonly FFT_SIZE = 2048;
  /** Scales the 0..1 RMS into the 0..100 meter range. */
  private readonly RMS_SCALE = 100;
  /** Extra gain so normal speech fills a useful part of the meter. */
  private readonly LEVEL_GAIN = 1.8;
  /** Weight kept from the previous level when falling (rise is instant). */
  private readonly LEVEL_SMOOTHING = 0.8;
  /** How often the meter is allowed to repaint the view (ms). */
  private readonly METER_UI_INTERVAL_MS = 100;

  // -- Recording --
  /** Flush a chunk this often so the webm carries an accurate duration (ms). */
  private readonly RECORD_TIMESLICE_MS = 1000;
  /** Countdown re-evaluation interval (ms); value is read off wall-clock. */
  private readonly COUNTDOWN_TICK_MS = 250;
  /** How often the recording peak level is sampled (ms). */
  private readonly PEAK_SAMPLE_INTERVAL_MS = 100;
  /** Preferred recording container/codecs, best first. */
  private readonly MIME_CANDIDATES = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];

  // -- Network check --
  /** How often the network check repeats while the panel is open (ms). */
  private readonly NETWORK_CHECK_INTERVAL_MS = 10000;
  private readonly DOWNLINK_GOOD_MBPS = 5;
  private readonly DOWNLINK_FAIR_MBPS = 1.5;
  /** With a good downlink, latency must stay under this to still rate good. */
  private readonly LATENCY_GOOD_CAP_MS = 300;
  private readonly LATENCY_GOOD_MS = 150;
  private readonly LATENCY_FAIR_MS = 500;

  // ===================== Device-test state =====================

  testing = false;
  initializing = false;
  errorMessage = '';

  // ===================== Level-meter state =====================

  micLevel = 0;
  micDetected = false;

  /**
   * Microphone card state for the status panel. The mic only "passes" once the
   * user actually speaks, so before any voice is picked up we show a neutral
   * "waiting" state instead of a red "Not working" — the mic isn't broken, it
   * just hasn't heard anything yet.
   */
  get micStatus(): 'checking' | 'waiting' | 'detected' | 'error' {
    // Once a clip has been recorded, its result is the source of truth for the
    // tile. The live meter keeps running during playback, so ambient noise or
    // speaker feedback could otherwise re-latch "detected" and contradict a
    // "couldn't detect your voice" outcome.
    if (this.hasRecording) {
      return this.voiceDetected ? 'detected' : 'error';
    }
    if (this.initializing) {
      return 'checking';
    }
    if (this.micDetected) {
      return 'detected';
    }
    // Preview is live and we're listening — voice just hasn't crossed the
    // threshold yet. Only a failed device acquisition (no live preview) is a
    // genuine error.
    return this.testing ? 'waiting' : 'error';
  }

  readonly levelSegments = Array.from({ length: 20 });
  segmentClasses: string[] = this.levelSegments.map(() => 'bg-slate_50');

  // ===================== Recording state =====================

  recording = false;
  hasRecording = false;
  recordedUrl: string | null = null;
  voiceDetected = false;
  recordCountdown = 0;
  consentChecked = false;

  /** Seconds recorded so far in the current clip. */
  get recordedElapsed(): number {
    return Math.max(0, this.recordSeconds - this.recordCountdown);
  }
  /** Whether enough has been recorded to allow stopping. */
  get canStopRecording(): boolean {
    return this.recordedElapsed >= this.minRecordSeconds;
  }

  /** Consent is only valid after a successful recording with detected voice. */
  get canConfirmConsent(): boolean {
    return this.hasRecording && this.voiceDetected;
  }

  // ===================== Network state =====================

  /** A network connectivity check is currently running. */
  networkChecking = false;
  networkOnline = true;
  networkLatencyMs: number | null = null;
  networkDownlinkMbps: number | null = null;
  networkQuality: 'good' | 'fair' | 'poor' | 'offline' | null = null;

  // ===================== Private runtime handles =====================

  private stream?: MediaStream;
  private audioContext?: AudioContext;
  private source?: MediaStreamAudioSourceNode;
  private analyser?: AnalyserNode;
  private rafId?: number;
  private mediaRecorder?: MediaRecorder;
  private chunks: BlobPart[] = [];
  private recordTimer?: ReturnType<typeof setInterval>;
  private peakTimer?: ReturnType<typeof setInterval>;
  private networkTimer?: ReturnType<typeof setInterval>;
  private networkAbort?: AbortController;
  /** True once the view is torn down — gates out-of-zone work and repaints. */
  private destroyed = false;

  constructor(
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  // ===================== Lifecycle =====================

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      // Show the recording container straight away (no extra "Test" click) and
      // run the network check alongside the device check.
      this.startTest();
      this.checkNetwork();
    });

    this.networkTimer = this.runInterval(
      () => this.checkNetwork(),
      this.NETWORK_CHECK_INTERVAL_MS,
    );

    // Register out of zone so a network flap doesn't tick the whole app.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopTest();
    if (this.networkTimer) {
      clearInterval(this.networkTimer);
      this.networkTimer = undefined;
    }
    this.networkAbort?.abort();
    this.networkChecking = false;
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
  }

  // ===================== Device test (camera / mic) =====================

  /** Start the live camera preview + mic level meter. */
  async startTest(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.errorMessage = '';
    this.micDetected = false;
    this.initializing = true;
    this.clearRecording();
    this.repaint();

    // Run device acquisition out of zone: the getUserMedia await and the meter
    // it kicks off must not each trigger an app-wide change-detection pass.
    await this.zone.runOutsideAngular(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: true,
        });

        // The component may have been torn down while the permission prompt was
        // open — don't leave the camera/mic running on a dead component.
        if (this.destroyed) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        this.stream = stream;
        this.initializing = false;
        this.testing = true;
        this.repaint();

        // Attach the feed once *ngIf="testing" has rendered the <video>.
        queueMicrotask(() => this.showLivePreview());
        this.startMeter(stream);
      } catch (error: any) {
        this.testing = false;
        this.initializing = false;
        if (error?.name === 'NotAllowedError') {
          this.errorMessage =
            'Camera/microphone access is blocked. Please allow it in your browser settings and try again.';
        } else if (error?.name === 'NotFoundError') {
          this.errorMessage =
            'No microphone or camera was found. Please connect a device and try again.';
        } else {
          this.errorMessage =
            'Unable to access your camera/microphone. Please check your device and try again.';
        }
        this.repaint();
      }
    });
  }

  /** Tear everything down and reset the UI. */
  stopTest(): void {
    this.stopRecording();
    this.stopMeter();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = undefined;
    if (this.previewVideo?.nativeElement) {
      this.previewVideo.nativeElement.srcObject = null;
    }
    this.clearRecording();
    this.testing = false;
    this.micLevel = 0;
    this.micDetected = false;
    this.resetSegments();
  }

  /** Show the live camera feed in the shared video element. */
  private showLivePreview(): void {
    const el = this.previewVideo?.nativeElement;
    if (!el) {
      return;
    }
    el.controls = false;
    el.muted = true;
    el.src = '';
    el.srcObject = this.stream ?? null;
    el.play?.().catch(() => {});
  }

  // ===================== Level meter =====================

  private startMeter(stream: MediaStream): void {
    if (!stream.getAudioTracks().length) {
      return;
    }
    this.audioContext = new AudioContext();
    this.source = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = this.FFT_SIZE;
    this.source.connect(this.analyser);

    // A context created off the user-gesture stack can come up suspended
    // (autoplay policy); resuming makes the analyser read live samples.
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }

    const time = new Uint8Array(this.analyser.fftSize);
    let lastPaintAt = 0;
    let lastShownLevel = -1;
    let lastShownDetected = false;

    const tick = () => {
      if (!this.analyser) {
        return;
      }

      // Overall input level (RMS) drives both the meter and the latch.
      this.analyser.getByteTimeDomainData(time);
      let sum = 0;
      for (let i = 0; i < time.length; i++) {
        const v = (time[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / time.length) * this.RMS_SCALE;
      const target = Math.min(100, Math.round(rms * this.LEVEL_GAIN));
      // Rise fast, fall smoothly so the meter doesn't snap to zero.
      this.micLevel =
        target >= this.micLevel
          ? target
          : Math.round(
              this.micLevel * this.LEVEL_SMOOTHING +
                target * (1 - this.LEVEL_SMOOTHING),
            );
      if (this.micLevel >= this.voiceThreshold) {
        this.micDetected = true;
      }

      // We run outside Angular, so publish a throttled local repaint only when
      // the values the template shows actually changed.
      const now = performance.now();
      if (
        now - lastPaintAt >= this.METER_UI_INTERVAL_MS &&
        (this.micLevel !== lastShownLevel ||
          this.micDetected !== lastShownDetected)
      ) {
        lastPaintAt = now;
        lastShownLevel = this.micLevel;
        lastShownDetected = this.micDetected;
        this.refreshSegments();
        this.repaint();
      }

      this.rafId = requestAnimationFrame(tick);
    };

    this.zone.runOutsideAngular(() => tick());
  }

  private stopMeter(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
    // Tear the graph down deterministically rather than relying on close().
    this.source?.disconnect();
    this.analyser?.disconnect();
    this.source = undefined;
    this.analyser = undefined;
    this.audioContext?.close().catch(() => {});
    this.audioContext = undefined;
  }

  /** Tailwind class for one meter row given its level position (0 = lowest). */
  private segmentClassFor(position: number): string {
    const count = this.levelSegments.length;
    const threshold = ((position + 1) / count) * 100;
    if (this.micLevel < threshold) {
      return 'bg-slate_50';
    }
    return 'bg-primary_Bg_Button_Color';
  }

  /** Rebuild {@link segmentClasses}; row 0 is the top, so level fills bottom-up. */
  private refreshSegments(): void {
    const count = this.levelSegments.length;
    const next = new Array<string>(count);
    for (let i = 0; i < count; i++) {
      next[i] = this.segmentClassFor(count - 1 - i);
    }
    this.segmentClasses = next;
  }

  private resetSegments(): void {
    this.segmentClasses = this.levelSegments.map(() => 'bg-slate_50');
  }

  // ===================== Clip recording & playback =====================

  /** Record a short clip so the candidate can play back and hear their voice. */
  startRecording(): void {
    if (!this.stream || this.recording) {
      return;
    }
    this.clearRecording();
    // A fresh take re-tests the mic: drop the detection latched during the
    // previous take so the Microphone tile reflects this recording only.
    this.micDetected = false;
    // Switch the single video element back to the live camera feed.
    this.showLivePreview();
    // A user click is the chance to unlock a still-suspended audio context.
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }
    this.chunks = [];
    this.voiceDetected = false;

    let peak = 0;
    this.peakTimer = this.runInterval(() => {
      if (this.micLevel > peak) {
        peak = this.micLevel;
      }
    }, this.PEAK_SAMPLE_INTERVAL_MS);

    const mimeType = this.pickMimeType();
    try {
      // Build the recorder out of zone so its per-second ondataavailable
      // callbacks don't each tick the whole app while recording.
      this.mediaRecorder = this.zone.runOutsideAngular(() =>
        mimeType
          ? new MediaRecorder(this.stream!, { mimeType })
          : new MediaRecorder(this.stream!),
      );
    } catch {
      this.clearPeakTimer();
      this.errorMessage =
        'Recording is not supported on this browser. Please try a different browser.';
      this.repaint();
      return;
    }

    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) {
        this.chunks.push(e.data);
      }
    };
    this.mediaRecorder.onstop = () => {
      this.clearPeakTimer();
      // The 'stop' event is async — it can arrive after teardown.
      if (this.destroyed) {
        return;
      }
      this.voiceDetected = peak >= this.voiceThreshold;
      const blob = new Blob(this.chunks, {
        type: this.mediaRecorder?.mimeType || mimeType || 'video/webm',
      });
      this.recordedUrl = URL.createObjectURL(blob);
      this.hasRecording = true;
      this.recording = false;
      this.recordCountdown = 0;
      // Play the recording back in the same video element.
      this.showRecordedClip();
      this.recordingCompleted.emit(this.voiceDetected);
      this.repaint();
    };

    this.recording = true;
    this.recordCountdown = this.recordSeconds;
    this.mediaRecorder.start(this.RECORD_TIMESLICE_MS);

    // Drive the countdown/stop off real elapsed time so the captured clip is a
    // full `recordSeconds` of wall-clock, immune to setInterval drift. Repaint
    // locally only when the displayed second actually ticks.
    const startedAt = performance.now();
    this.recordTimer = this.runInterval(() => {
      const elapsed = (performance.now() - startedAt) / 1000;
      const next = Math.max(0, Math.ceil(this.recordSeconds - elapsed));
      if (next !== this.recordCountdown) {
        this.recordCountdown = next;
        this.repaint();
      }
      if (elapsed >= this.recordSeconds) {
        this.stopRecording();
      }
    }, this.COUNTDOWN_TICK_MS);

    this.repaint();
  }

  /** Stop the current recording early (or when the countdown ends). */
  stopRecording(): void {
    if (this.recordTimer) {
      clearInterval(this.recordTimer);
      this.recordTimer = undefined;
    }
    this.clearPeakTimer();
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  /** Play the just-recorded clip in the same shared video element. */
  private showRecordedClip(): void {
    const el = this.previewVideo?.nativeElement;
    if (!el || !this.recordedUrl) {
      return;
    }
    el.srcObject = null;
    el.muted = false;
    el.controls = true;
    el.src = this.recordedUrl;
    el.load();
  }

  /** Candidate confirms (via checkbox) they listened to their recording. */
  onConsentChange(checked: boolean): void {
    if (!this.canConfirmConsent) {
      this.consentChecked = false;
      this.testPassed.emit(false);
      this.repaint();
      return;
    }

    this.consentChecked = checked;
    this.testPassed.emit(checked);
    this.repaint();
  }

  onContinueClick(): void {
    this.continueClicked.emit();
  }

  private clearRecording(): void {
    if (this.recordedUrl) {
      URL.revokeObjectURL(this.recordedUrl);
    }
    this.recordedUrl = null;
    this.hasRecording = false;
    this.voiceDetected = false;
    // Recording was cleared/re-started — any prior consent is no longer valid.
    if (this.consentChecked) {
      this.consentChecked = false;
      this.testPassed.emit(false);
    }
    this.recordingCompleted.emit(false);
  }

  private clearPeakTimer(): void {
    if (this.peakTimer) {
      clearInterval(this.peakTimer);
      this.peakTimer = undefined;
    }
  }

  /** First supported recording mime type, or undefined to let the browser pick. */
  private pickMimeType(): string | undefined {
    if (
      typeof MediaRecorder === 'undefined' ||
      typeof MediaRecorder.isTypeSupported !== 'function'
    ) {
      return undefined;
    }
    return this.MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
  }

  // ===================== Network connectivity =====================
  
  checkNetwork(): void {
    if (this.destroyed) {
      return;
    }
    if (this.networkChecking) {
      return;
    }
    this.networkChecking = true;
    // Show the "Checking…" indicator while the probe is in flight. This is a
    // single LOCAL repaint (not an app-wide tick), so it doesn't reintroduce
    // the CD storm; the resolved values are published at the terminal branch.
    this.repaint();

    // Run the whole check (including the fetch) outside Angular so the fetch
    // resolution doesn't trigger an app-wide CD pass; publish once at the end.
    this.zone.runOutsideAngular(async () => {
      this.networkOnline = navigator.onLine;

      if (!this.networkOnline) {
        this.networkQuality = 'offline';
        this.networkLatencyMs = null;
        this.networkDownlinkMbps = null;
        this.networkChecking = false;
        this.repaint();
        return;
      }

      // Estimated bandwidth from the Network Information API when available.
      const conn = (navigator as any)?.connection;
      this.networkDownlinkMbps =
        typeof conn?.downlink === 'number' ? conn.downlink : null;

      this.networkAbort = new AbortController();
      this.networkLatencyMs = await this.measureLatency(
        this.networkAbort.signal,
      );

      if (this.destroyed) {
        return;
      }
      // A measurable round trip while online still counts as connected even if
      // the Network Information API isn't available on this browser.
      this.networkQuality = this.scoreNetwork(
        this.networkLatencyMs,
        this.networkDownlinkMbps,
      );
      this.networkChecking = false;
      this.repaint();
    });
  }

  /** One cache-busted round trip to the app origin (ms); null if it fails. */
  private async measureLatency(signal: AbortSignal): Promise<number | null> {
    const start = performance.now();
    try {
      await fetch(`${location.origin}/favicon.ico?_n=${start}`, {
        cache: 'no-store',
        signal,
      });
      return Math.round(performance.now() - start);
    } catch {
      // Failed/aborted request => unknown latency.
      return null;
    }
  }

  /** Bucket the measurements into a good/fair/poor quality rating. */
  private scoreNetwork(
    latency: number | null,
    downlink: number | null,
  ): 'good' | 'fair' | 'poor' {
    if (downlink != null) {
      if (
        downlink >= this.DOWNLINK_GOOD_MBPS &&
        (latency == null || latency < this.LATENCY_GOOD_CAP_MS)
      ) {
        return 'good';
      }
      if (downlink >= this.DOWNLINK_FAIR_MBPS) {
        return 'fair';
      }
      return 'poor';
    }
    // No bandwidth estimate — fall back to latency alone.
    if (latency == null) {
      return 'fair';
    }
    if (latency < this.LATENCY_GOOD_MS) {
      return 'good';
    }
    if (latency < this.LATENCY_FAIR_MS) {
      return 'fair';
    }
    return 'poor';
  }

  private readonly onlineHandler = () => this.checkNetwork();
  private readonly offlineHandler = () => {
    this.networkOnline = false;
    this.networkQuality = 'offline';
    this.networkLatencyMs = null;
    this.networkDownlinkMbps = null;
    this.repaint();
  };

  // ===================== Helpers =====================

  /** Repaint this component only (no app-wide tick); safe after destroy. */
  private repaint(): void {
    if (this.destroyed) {
      return;
    }
    this.cdr.detectChanges();
  }

  /**
   * Create a setInterval whose ticks run OUTSIDE Angular, so they never trigger
   * an app-wide change-detection pass. Callers publish UI via {@link repaint}.
   */
  private runInterval(
    handler: () => void,
    ms: number,
  ): ReturnType<typeof setInterval> {
    return this.zone.runOutsideAngular(() => setInterval(handler, ms));
  }
}
